/**
 * SCENARIO CONSTANTS
 * 
 * Define os 3 cenários (Pessimista, Base, Otimista) para análise de sensibilidade
 * Baseado no modelo Excel: Valuation_Setima_555_Completo.xlsx
 * 
 * @version 1.0.0
 */

import { ValuationInputs, MacroAssumptions, CompanyAssumptions } from '../types/valuation';

// ============================================================================
// PREMISSAS MACROECONÔMICAS (compartilhadas por todos os cenários)
// ============================================================================

export const DEFAULT_MACRO_ASSUMPTIONS: MacroAssumptions = {
  taxaSelic: 0.1175,           // 11,75%
  inflacao: 0.045,             // 4,5%
  crescimentoPIB: 0.025,       // 2,5%
  premioRiscoBrasil: 0.035,    // 3,5%
  premioRiscoMercado: 0.08,    // 8%
};

// ============================================================================
// CENÁRIO BASE (Realista)
// ============================================================================

export const BASE_SETIMA: CompanyAssumptions = {
  nome: 'Sétima',
  baseYear: 2024,              // Ano base para projeções
  receitaBase: 10_000_000,     // R$ 10M (2024)
  ebitdaBase: 3_500_000,       // R$ 3.5M (2024)
  
  crescimento: {
    2025: 0.15,                // 15%
    2026: 0.12,                // 12%
    2027: 0.10,                // 10%
    2028: 0.08,                // 8%
    2029: 0.06,                // 6%
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  
  margemEBITDA: {
    2025: 0.35,                // 35%
    2026: 0.36,                // 36%
    2027: 0.37,                // 37%
    2028: 0.38,                // 38%
    2029: 0.39,                // 39%
  },
  
  depreciacao_pct_receita: 0.03,  // 3% (baseado em histórico)
  capex_pct_receita: {
    2025: 0.05,
    2026: 0.05,
    2027: 0.05,
    2028: 0.05,
    2029: 0.05,
  },
  nwc_pct_delta_receita: {
    2025: 0.035,
    2026: 0.035,
    2027: 0.035,
    2028: 0.035,
    2029: 0.035,
  },
  taxaImpostoEBIT: 0.18,          // 18%
  beta: 1.2,
  wacc: 0.145,                    // 14,5%
  g: 0.03,                        // 3% (perpetuidade)
  kpd_in_wacc: false,
};

export const BASE_555: CompanyAssumptions = {
  nome: '555 Studios',
  baseYear: 2024,              // Ano base para projeções
  receitaBase: 5_000_000,      // R$ 5M (2024)
  ebitdaBase: 1_500_000,       // R$ 1.5M (2024)
  
  crescimento: {
    2025: 0.20,                // 20%
    2026: 0.18,                // 18%
    2027: 0.15,                // 15%
    2028: 0.12,                // 12%
    2029: 0.10,                // 10%
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  
  margemEBITDA: {
    2025: 0.30,                // 30%
    2026: 0.31,                // 31%
    2027: 0.32,                // 32%
    2028: 0.33,                // 33%
    2029: 0.34,                // 34%
  },
  
  depreciacao_pct_receita: 0.02,  // 2%
  capex_pct_receita: {
    2025: 0.20,
    2026: 0.18,
    2027: 0.16,
    2028: 0.15,
    2029: 0.15,
  },
  nwc_pct_delta_receita: {
    2025: 0.04,
    2026: 0.04,
    2027: 0.04,
    2028: 0.04,
    2029: 0.04,
  },
  taxaImpostoEBIT: 0.18,          // 18%
  beta: 1.4,
  wacc: 0.165,                    // 16,5%
  g: 0.03,                        // 3%
  kpd_in_wacc: false,
};

export const BASE_INPUTS: ValuationInputs = {
  macro: DEFAULT_MACRO_ASSUMPTIONS,
  setima: BASE_SETIMA,
  studios555: BASE_555,
  equityBridge: {
    dividaLiquida: 0,
    caixaExcedente: 0,
  },
  discounts: {
    kpd_mode: 'in_wacc',       // ✅ PADRONIZADO: KPD no WACC (evita dupla contagem)
    kpd_rate: 0.15,            // 15%
    dloc: 0.10,                // 10%
    dlom: 0.27,                // 27%
  },
  stake: {
    percentual: 1.0,           // 100% (participação integral)
  },
};

// ============================================================================
// CENÁRIO PESSIMISTA (Conservador)
// ============================================================================

export const PESSIMISTA_SETIMA: CompanyAssumptions = {
  ...BASE_SETIMA,
  
  crescimento: {
    2025: 0.08,                // 8% (-47% vs Base)
    2026: 0.07,                // 7%
    2027: 0.06,                // 6%
    2028: 0.05,                // 5%
    2029: 0.04,                // 4%
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  
  margemEBITDA: {
    2025: 0.32,                // 32% (-3pp vs Base)
    2026: 0.33,                // 33%
    2027: 0.34,                // 34%
    2028: 0.35,                // 35%
    2029: 0.36,                // 36%
  },
  
  wacc: 0.155,                 // 15,5% (+1pp vs Base - maior risco)
  g: 0.025,                    // 2,5% (-0,5pp vs Base)
};

export const PESSIMISTA_555: CompanyAssumptions = {
  ...BASE_555,
  
  crescimento: {
    2025: 0.12,                // 12% (-40% vs Base)
    2026: 0.10,                // 10%
    2027: 0.08,                // 8%
    2028: 0.07,                // 7%
    2029: 0.06,                // 6%
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  
  margemEBITDA: {
    2025: 0.27,                // 27% (-3pp vs Base)
    2026: 0.28,                // 28%
    2027: 0.29,                // 29%
    2028: 0.30,                // 30%
    2029: 0.31,                // 31%
  },
  
  wacc: 0.175,                 // 17,5% (+1pp vs Base)
  g: 0.025,                    // 2,5%
};

export const PESSIMISTA_INPUTS: ValuationInputs = {
  ...BASE_INPUTS,
  setima: PESSIMISTA_SETIMA,
  studios555: PESSIMISTA_555,
  discounts: {
    ...BASE_INPUTS.discounts,
    kpd_rate: 0.20,            // 20% (maior desconto)
    dloc: 0.15,                // 15%
    dlom: 0.30,                // 30%
  },
};

// ============================================================================
// CENÁRIO OTIMISTA (Agressivo)
// ============================================================================

export const OTIMISTA_SETIMA: CompanyAssumptions = {
  ...BASE_SETIMA,
  
  crescimento: {
    2025: 0.20,                // 20% (+33% vs Base)
    2026: 0.18,                // 18%
    2027: 0.15,                // 15%
    2028: 0.12,                // 12%
    2029: 0.10,                // 10%
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  
  margemEBITDA: {
    2025: 0.38,                // 38% (+3pp vs Base)
    2026: 0.39,                // 39%
    2027: 0.40,                // 40%
    2028: 0.41,                // 41%
    2029: 0.42,                // 42%
  },
  
  wacc: 0.135,                 // 13,5% (-1pp vs Base - menor risco)
  g: 0.035,                    // 3,5% (+0,5pp vs Base)
};

export const OTIMISTA_555: CompanyAssumptions = {
  ...BASE_555,
  
  crescimento: {
    2025: 0.30,                // 30% (+50% vs Base)
    2026: 0.25,                // 25%
    2027: 0.20,                // 20%
    2028: 0.18,                // 18%
    2029: 0.15,                // 15%
    perpetuo: 0.03,  // Taxa perpetuidade
  },
  
  margemEBITDA: {
    2025: 0.33,                // 33% (+3pp vs Base)
    2026: 0.34,                // 34%
    2027: 0.35,                // 35%
    2028: 0.36,                // 36%
    2029: 0.37,                // 37%
  },
  
  wacc: 0.155,                 // 15,5% (-1pp vs Base)
  g: 0.035,                    // 3,5%
};

export const OTIMISTA_INPUTS: ValuationInputs = {
  ...BASE_INPUTS,
  setima: OTIMISTA_SETIMA,
  studios555: OTIMISTA_555,
  discounts: {
    ...BASE_INPUTS.discounts,
    kpd_rate: 0.10,            // 10% (menor desconto)
    dloc: 0.05,                // 5%
    dlom: 0.20,                // 20%
  },
};

// ============================================================================
// MAPA DE CENÁRIOS
// ============================================================================

export type ScenarioKey = 'pessimista' | 'base' | 'otimista';

export interface ScenarioConfig {
  key: ScenarioKey;
  label: string;
  description: string;
  color: string;
  inputs: ValuationInputs;
}

export const SCENARIOS: Record<ScenarioKey, ScenarioConfig> = {
  pessimista: {
    key: 'pessimista',
    label: 'Pessimista',
    description: 'Cenário conservador com crescimento reduzido e margens menores',
    color: '#dc2626',        // Red dessaturado (era #f44336 → #dc2626, -10% saturation)
    inputs: PESSIMISTA_INPUTS,
  },
  base: {
    key: 'base',
    label: 'Base',
    description: 'Cenário realista baseado em análise histórica e projeções',
    color: '#2563eb',        // Blue dessaturado (era #2196f3 → #2563eb, -12% saturation)
    inputs: BASE_INPUTS,
  },
  otimista: {
    key: 'otimista',
    label: 'Otimista',
    description: 'Cenário agressivo com forte crescimento e expansão de margens',
    color: '#059669',        // Green dessaturado (era #4caf50 → #059669, -15% saturation)
    inputs: OTIMISTA_INPUTS,
  },
};

export const SCENARIO_KEYS: ScenarioKey[] = ['pessimista', 'base', 'otimista'];

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Obtém configuração de um cenário
 */
export function getScenarioConfig(key: ScenarioKey): ScenarioConfig {
  return SCENARIOS[key];
}

/**
 * Obtém todos os cenários como array
 */
export function getAllScenarios(): ScenarioConfig[] {
  return SCENARIO_KEYS.map(key => SCENARIOS[key]);
}

/**
 * Calcula variação percentual entre cenários
 */
export function calculateVariation(
  value: number,
  baseValue: number
): { absolute: number; percentage: number } {
  const absolute = value - baseValue;
  const percentage = baseValue !== 0 ? (absolute / baseValue) * 100 : 0;
  
  return { absolute, percentage };
}
